/** SUNTRONIX ONE — ONE LEDGER. Simple manual bank transaction register.
 *  Personal + Business (per-establishment) money-in / money-out / transfer.
 *  Reused by Finance → ONE Ledger and My ONE → ONE Ledger.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, ArrowLeftRight, Plus, Search,
  FileSpreadsheet, FileText, Printer, Building2, User, Landmark, Loader2, X,
  Paperclip, Upload, Check, Pencil,
} from "lucide-react";

const fmt = (n, cur = "INR") => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(n);
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const today = () => new Date().toISOString().slice(0, 10);
const MODES = ["NEFT","RTGS","IMPS","UPI","CHEQUE","CASH","CARD","AUTO_DEBIT","OTHER"];

export default function OneLedger({ defaultScope = "BUSINESS" }) {
  const [scope, setScope] = useState(defaultScope);
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState("home");
  const [simple, setSimple] = useState(() => { try { return localStorage.getItem("st.ledger.simple") === "1"; } catch { return false; } });
  const [accounts, setAccounts] = useState([]);
  const [home, setHome] = useState(null);
  const [entry, setEntry] = useState(null);     // {kind:'PAID'|'RECEIVED'|'TRANSFER'}
  const [showAccount, setShowAccount] = useState(false);
  const [editAccount, setEditAccount] = useState(null);

  useEffect(() => { api.get("/ledger/access").then(({ data }) => setAccess(data)).catch(() => {}); }, []);

  const loadHome = useCallback(() => {
    api.get(`/ledger/home?scope=${scope}`).then(({ data }) => { setHome(data); setAccounts(data.accounts || []); })
      .catch((e) => { setHome(null); if (e.response?.status === 422) toast.info("Select an establishment in the top bar to view the Business ledger."); });
  }, [scope]);
  useEffect(() => { loadHome(); }, [loadHome]);

  const toggleSimple = () => { const v = !simple; setSimple(v); try { localStorage.setItem("st.ledger.simple", v ? "1" : "0"); } catch {} };

  if (access && scope === "PERSONAL" && !access.personal) {
    // fall back handled below in body
  }

  const TABS = simple
    ? [["home", "Today"], ["ledger", "View Ledger"]]
    : [["home", "Home"], ["simple", "Simple Entry"], ["ledger", "Classic Ledger"],
       ["month", "Month"], ["year", "Year"], ["reports", "Reports"],
       ["recurring", "Recurring"], ["import", "Import"], ["reconcile", "Reconcile"]];

  return (
    <div className={"p-4 md:p-6 " + (simple ? "max-w-3xl mx-auto" : "")} data-testid="one-ledger-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><Wallet className="h-5 w-5" /></div>
          <div>
            <h1 className={"font-bold tracking-tight text-slate-900 " + (simple ? "text-3xl" : "text-2xl")} style={{ fontFamily: "Manrope, sans-serif" }}>ONE LEDGER</h1>
            <p className="text-[13px] text-slate-500">Your simple bank transaction register.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Scope switch */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="ledger-scope-switch">
            {[["BUSINESS", Building2, "Business"], ["PERSONAL", User, "Personal"]].map(([s, Icon, label]) => (
              <button key={s} onClick={() => setScope(s)} data-testid={`ledger-scope-${s}`}
                className={"inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[13px] font-medium transition-colors " +
                  (scope === s ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50")}>
                <Icon className="h-3.5 w-3.5" />{label}
              </button>
            ))}
          </div>
          <button onClick={toggleSimple} data-testid="ledger-simple-toggle"
            className={"h-8 px-3 rounded-lg border text-[13px] font-medium " + (simple ? "bg-amber-100 border-amber-300 text-amber-800" : "border-slate-200 text-slate-600 hover:bg-slate-50")}>
            {simple ? "Simple Mode: ON" : "Simple Mode"}
          </button>
        </div>
      </div>

      {access && scope === "PERSONAL" && !access.personal ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500" data-testid="ledger-personal-denied">
          Personal Ledger needs Personal Finance permission. Ask a Super Admin to enable it for your account.
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex flex-wrap gap-1.5 mb-4 border-b border-slate-200">
            {TABS.map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`ledger-tab-${k}`}
                className={"px-3.5 h-9 text-[13px] font-medium -mb-px border-b-2 transition-colors " +
                  (tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800")}>
                {label}
              </button>
            ))}
          </div>

          {/* Quick entry buttons (Home, Simple Entry tab, and Simple Mode) */}
          {(tab === "home" || tab === "simple" || simple) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="ledger-quick-entry">
              <QuickBtn testid="qe-paid" tone="rose" icon={ArrowUpCircle} label="Money Paid" big={simple} onClick={() => setEntry({ kind: "PAID" })} />
              <QuickBtn testid="qe-received" tone="emerald" icon={ArrowDownCircle} label="Money Received" big={simple} onClick={() => setEntry({ kind: "RECEIVED" })} />
              <QuickBtn testid="qe-transfer" tone="sky" icon={ArrowLeftRight} label="Transfer" big={simple} onClick={() => setEntry({ kind: "TRANSFER" })} />
            </div>
          )}

          {tab === "home" && <HomeView home={home} simple={simple} onAddAccount={() => setShowAccount(true)} onEditAccount={setEditAccount} />}
          {tab === "simple" && !simple && (
            <p className="text-[13px] text-slate-500" data-testid="simple-entry-hint">Tap a button above to record a transaction. Tip: use <b>Save &amp; add next</b> to enter many quickly.</p>
          )}
          {(tab === "ledger") && <LedgerTable scope={scope} accounts={accounts} onChanged={loadHome} readOnlyHint={simple} />}
          {tab === "month" && <MonthView scope={scope} accounts={accounts} />}
          {tab === "year" && <YearView scope={scope} />}
          {tab === "reports" && <ReportsView scope={scope} accounts={accounts} />}
          {tab === "recurring" && <RecurringView scope={scope} accounts={accounts} onChanged={loadHome} />}
          {tab === "import" && <ImportView scope={scope} accounts={accounts} onChanged={loadHome} />}
          {tab === "reconcile" && <ReconcileView scope={scope} accounts={accounts} onChanged={loadHome} />}
        </>
      )}

      {entry && (
        <EntryDialog kind={entry.kind} scope={scope} accounts={accounts}
          onClose={() => setEntry(null)} onSaved={loadHome} />
      )}
      {showAccount && (
        <AccountDialog scope={scope} onClose={() => setShowAccount(false)} onSaved={() => { setShowAccount(false); loadHome(); }} />
      )}
      {editAccount && (
        <AccountDialog scope={scope} account={editAccount} onClose={() => setEditAccount(null)} onSaved={() => { setEditAccount(null); loadHome(); }} />
      )}
    </div>
  );
}

function QuickBtn({ testid, tone, icon: Icon, label, big, onClick }) {
  const tones = {
    rose: "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100",
    sky: "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100",
  };
  return (
    <button data-testid={testid} onClick={onClick}
      className={"flex items-center justify-center gap-2 rounded-xl border font-semibold transition-colors " +
        tones[tone] + (big ? " h-20 text-lg" : " h-14 text-[15px]")}>
      <Icon className={big ? "h-7 w-7" : "h-5 w-5"} />{label}
    </button>
  );
}

function Stat({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-[11px] text-slate-500 font-medium">{label}</div>
      <div className={"text-[20px] font-bold mt-1 tabular-nums " + tone}>{value}</div>
    </div>
  );
}

function HomeView({ home, simple, onAddAccount, onEditAccount }) {
  if (!home) return <div className="text-slate-400 py-6 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="home-today-received" label="Today · Received" value={fmt(home.today.received)} tone="text-emerald-700" />
        <Stat testid="home-today-paid" label="Today · Paid" value={fmt(home.today.paid)} tone="text-rose-700" />
        <Stat testid="home-month-received" label="This Month · Received" value={fmt(home.month.received)} tone="text-emerald-700" />
        <Stat testid="home-month-paid" label="This Month · Paid" value={fmt(home.month.paid)} tone="text-rose-700" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[13px] font-semibold text-slate-700">Bank Accounts</h3>
          <Button size="sm" variant="outline" onClick={onAddAccount} data-testid="home-add-account"><Plus className="h-4 w-4 mr-1" />Add account</Button>
        </div>
        {home.accounts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-[13px] text-slate-500">
            No bank accounts yet. Add one to start recording transactions.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="home-accounts">
            {home.accounts.map((a) => (
              <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 cursor-pointer hover:border-slate-300 transition-colors" data-testid={`account-card-${a.id}`} onClick={() => onEditAccount && onEditAccount(a)}>
                <div className="flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-slate-800 text-[14px]">{a.nickname}</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">{a.bank_name} · {a.account_type}{a.account_masked ? ` · ${a.account_masked}` : ""}</div>
                <div className="text-[19px] font-bold text-slate-900 mt-2 tabular-nums">{fmt(a.balance, a.currency)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!simple && (
        <div>
          <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Recent transactions {home.unreconciled ? <span className="ml-2 text-[11px] text-amber-600">· {home.unreconciled} unreconciled</span> : null}</h3>
          <RecentList rows={home.recent} />
        </div>
      )}
    </div>
  );
}

function RecentList({ rows }) {
  if (!rows?.length) return <div className="text-[13px] text-slate-400">No transactions yet.</div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="recent-list">
      {rows.map((t) => (
        <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
          <span className="text-[12px] text-slate-400 w-20 shrink-0">{t.date}</span>
          <span className="flex-1 truncate text-[13px] text-slate-800">{t.particulars}{t.party ? <span className="text-slate-400"> · {t.party}</span> : null}</span>
          <span className={"text-[13px] font-semibold tabular-nums " + (t.debit ? "text-rose-700" : "text-emerald-700")}>
            {t.debit ? "− " + fmt(t.debit) : "+ " + fmt(t.credit)}
          </span>
        </div>
      ))}
    </div>
  );
}

function EntryDialog({ kind, scope, accounts, onClose, onSaved }) {
  const isTransfer = kind === "TRANSFER";
  const [f, setF] = useState({ date: today(), account_id: accounts[0]?.id || "", to_account_id: "", amount: "", party: "", particulars: "", category: "", mode: "", reference: "", notes: "", attachment_id: null, party_link_type: null, party_link_id: null, party_link_name: null });
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [attachName, setAttachName] = useState("");
  const amtRef = useRef(null);

  useEffect(() => { api.get(`/ledger/categories?scope=${scope}`).then(({ data }) => setCats(data.categories || [])).catch(() => {}); }, [scope]);
  useEffect(() => { setTimeout(() => amtRef.current?.focus(), 150); }, []);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const title = isTransfer ? "Transfer between accounts" : (kind === "PAID" ? "Money Paid" : "Money Received");

  const submit = async (addNext, confirmDup = false) => {
    if (!f.account_id) return toast.error("Select an account");
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter an amount");
    if (isTransfer && !f.to_account_id) return toast.error("Select destination account");
    if (!isTransfer && !f.particulars.trim()) return toast.error("Enter a description");
    setBusy(true);
    try {
      if (isTransfer) {
        await api.post("/ledger/transactions/transfer", { scope, from_account_id: f.account_id, to_account_id: f.to_account_id, date: f.date, amount: Number(f.amount), particulars: f.particulars, reference: f.reference });
      } else {
        const payload = { scope, account_id: f.account_id, date: f.date, kind, amount: Number(f.amount), party: f.party, particulars: f.particulars, category: f.category || null, mode: f.mode || null, reference: f.reference || null, notes: f.notes || null, attachment_id: f.attachment_id || null, party_link_type: f.party_link_type, party_link_id: f.party_link_id, party_link_name: f.party_link_name };
        const { data } = await api.post(`/ledger/transactions${confirmDup ? "?confirm_duplicate=true" : ""}`, payload);
        if (data.needs_confirmation) { setDup(data); setBusy(false); return; }
      }
      toast.success("Saved ✓");
      onSaved && onSaved();
      if (addNext) { setF((s) => ({ ...s, amount: "", party: "", particulars: "", reference: "", notes: "", attachment_id: null })); setAttachName(""); setDup(null); setTimeout(() => amtRef.current?.focus(), 100); }
      else onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="entry-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={f.date} onChange={(e) => upd("date", e.target.value)} data-testid="entry-date" /></Field>
          <Field label={isTransfer ? "From account" : "Account"}>
            <Select value={f.account_id} onChange={(v) => upd("account_id", v)} testid="entry-account"
              options={accounts.map((a) => [a.id, a.nickname])} placeholder="Select account" />
          </Field>
          {isTransfer ? (
            <Field label="To account">
              <Select value={f.to_account_id} onChange={(v) => upd("to_account_id", v)} testid="entry-to-account"
                options={accounts.filter((a) => a.id !== f.account_id).map((a) => [a.id, a.nickname])} placeholder="Select account" />
            </Field>
          ) : (
            <Field label={kind === "PAID" ? "Paid To" : "Received From"}>
              <PartyField scope={scope} value={f.party} link={f.party_link_id ? { type: f.party_link_type, name: f.party_link_name } : null}
                onText={(v) => setF((s) => ({ ...s, party: v, party_link_type: null, party_link_id: null, party_link_name: null }))}
                onPick={(r) => setF((s) => ({ ...s, party: r.name, party_link_type: r.type, party_link_id: r.id, party_link_name: r.name }))} />
            </Field>
          )}
          <Field label="Amount">
            <Input ref={amtRef} type="number" value={f.amount} onChange={(e) => upd("amount", e.target.value)} data-testid="entry-amount" placeholder="0.00"
              onKeyDown={(e) => { if (e.key === "Enter") submit(false); }} />
          </Field>
          <Field label="Description / Particulars" full>
            <Input value={f.particulars} onChange={(e) => upd("particulars", e.target.value)} data-testid="entry-particulars" placeholder={isTransfer ? "e.g. Move funds to savings" : "e.g. Paid Electricity Bill"} />
          </Field>
          {!isTransfer && <>
            <Field label="Category">
              <Select value={f.category} onChange={(v) => upd("category", v)} testid="entry-category" options={cats.map((c) => [c, c])} placeholder="Optional" />
            </Field>
            <Field label={kind === "PAID" ? "Payment Mode" : "Receipt Mode"}>
              <Select value={f.mode} onChange={(v) => upd("mode", v)} testid="entry-mode" options={MODES.map((m) => [m, m])} placeholder="Optional" />
            </Field>
          </>}
          <Field label="Reference"><Input value={f.reference} onChange={(e) => upd("reference", e.target.value)} data-testid="entry-reference" placeholder="Optional" /></Field>
          {!isTransfer && <Field label="Notes"><Input value={f.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="entry-notes" placeholder="Optional" /></Field>}
          {!isTransfer && (
            <Field label="Receipt / attachment" full>
              <div className="flex items-center gap-2">
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="entry-attachment"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    setUploading(true);
                    try { const fd = new FormData(); fd.append("file", file);
                      const { data } = await api.post("/ledger/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      upd("attachment_id", data.attachment_id); setAttachName(data.filename); toast.success("Attached ✓");
                    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                    finally { setUploading(false); }
                  }}
                  className="text-[12px] file:mr-2 file:h-8 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:text-white file:text-[12px]" />
                {uploading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                {attachName && !uploading && <span className="text-[11px] text-emerald-600 truncate max-w-[160px]">{attachName}</span>}
              </div>
            </Field>
          )}
        </div>

        {dup && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-800" data-testid="entry-dup-warning">
            {dup.message} Save anyway?
            <div className="mt-2 flex gap-2">
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => submit(false, true)} data-testid="entry-dup-confirm">Save anyway</Button>
              <Button size="sm" variant="outline" onClick={() => setDup(null)}>Cancel</Button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="entry-cancel">Cancel</Button>
          {!isTransfer && <Button variant="outline" disabled={busy} onClick={() => submit(true)} data-testid="entry-save-next">Save &amp; add next</Button>}
          <Button disabled={busy} onClick={() => submit(false)} data-testid="entry-save" className="bg-slate-900 hover:bg-slate-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartyField({ scope, value, link, onText, onPick }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (scope !== "BUSINESS" || !value || value.trim().length < 2 || link) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/ledger/party-search?scope=BUSINESS&q=${encodeURIComponent(value.trim())}`); setResults(data.results || []); setOpen(true); }
      catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [value, scope, link]);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const tone = { customer: "text-sky-600", vendor: "text-amber-600", contact: "text-violet-600" };
  return (
    <div className="relative" ref={boxRef}>
      <Input value={value} onChange={(e) => onText(e.target.value)} onFocus={() => results.length && setOpen(true)}
        data-testid="entry-party" placeholder={scope === "BUSINESS" ? "Type a name — links to a customer/vendor" : "Name (optional)"} />
      {link && <span className="absolute right-2 top-2 text-[10px] font-semibold uppercase text-emerald-600" data-testid="entry-party-linked">{link.type} ✓</span>}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="party-results">
          {results.map((r) => (
            <button key={`${r.type}-${r.id}`} type="button" onClick={() => { onPick(r); setOpen(false); }}
              data-testid={`party-pick-${r.id}`}
              className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between gap-3">
              <span className="text-[13px] text-slate-800 truncate">{r.name}</span>
              <span className={"text-[10px] font-medium uppercase shrink-0 " + (tone[r.type] || "text-slate-400")}>{r.subtitle || r.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function Field({ label, children, full }) {
  return <label className={"space-y-1 " + (full ? "col-span-2" : "")}><span className="text-[11px] font-medium text-slate-500">{label}</span>{children}</label>;
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300">
      <option value="">{placeholder}</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function AccountDialog({ scope, account, onClose, onSaved }) {
  const edit = !!account;
  const [f, setF] = useState(edit
    ? { bank_name: account.bank_name || "", nickname: account.nickname || "", account_type: account.account_type || "SAVINGS", account_number: "", branch: account.branch || "", ifsc: account.ifsc || "", currency: account.currency || "INR", opening_balance: String(account.opening_balance ?? "0"), opening_balance_date: account.opening_balance_date || today(), status: account.status || "ACTIVE" }
    : { bank_name: "", nickname: "", account_type: "SAVINGS", account_number: "", branch: "", ifsc: "", currency: "INR", opening_balance: "0", opening_balance_date: today(), status: "ACTIVE" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.bank_name.trim()) return toast.error("Bank name is required");
    setBusy(true);
    try {
      if (edit) {
        await api.patch(`/ledger/accounts/${account.id}`, { nickname: f.nickname, branch: f.branch, ifsc: f.ifsc, status: f.status, account_type: f.account_type, opening_balance: Number(f.opening_balance || 0), opening_balance_date: f.opening_balance_date });
        toast.success("Account updated ✓");
      } else {
        await api.post("/ledger/accounts", { scope, ...f, opening_balance: Number(f.opening_balance || 0) });
        toast.success("Account added ✓");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save account"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this account?")) return;
    setBusy(true);
    try { await api.delete(`/ledger/accounts/${account.id}`); toast.success("Account removed"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove account"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="account-dialog">
        <DialogHeader><DialogTitle>{edit ? "Edit Bank Account" : "Add Bank Account"}</DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-400 -mt-2">We never store passwords, OTP, PIN or CVV.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Bank name"><Input value={f.bank_name} onChange={(e) => upd("bank_name", e.target.value)} data-testid="acc-bank" disabled={edit} /></Field>
          <Field label="Nickname"><Input value={f.nickname} onChange={(e) => upd("nickname", e.target.value)} data-testid="acc-nickname" placeholder="e.g. Main Current" /></Field>
          <Field label="Account type">
            <Select value={f.account_type} onChange={(v) => upd("account_type", v)} testid="acc-type"
              options={["SAVINGS","CURRENT","CASH","CREDIT_CARD","LOAN","INVESTMENT","OTHER"].map((t) => [t, t])} placeholder="Type" />
          </Field>
          {edit ? (
            <Field label="Status"><Select value={f.status} onChange={(v) => upd("status", v)} testid="acc-status" options={[["ACTIVE","ACTIVE"],["INACTIVE","INACTIVE"]]} placeholder="Status" /></Field>
          ) : (
            <Field label="Account number (masked)"><Input value={f.account_number} onChange={(e) => upd("account_number", e.target.value)} data-testid="acc-number" placeholder="Only last 4 kept" /></Field>
          )}
          <Field label="Branch"><Input value={f.branch} onChange={(e) => upd("branch", e.target.value)} /></Field>
          <Field label="IFSC"><Input value={f.ifsc} onChange={(e) => upd("ifsc", e.target.value)} /></Field>
          <Field label="Opening balance"><Input type="number" value={f.opening_balance} onChange={(e) => upd("opening_balance", e.target.value)} data-testid="acc-opening" /></Field>
          <Field label="Opening balance date"><Input type="date" value={f.opening_balance_date} onChange={(e) => upd("opening_balance_date", e.target.value)} /></Field>
        </div>
        <DialogFooter className="gap-2">
          {edit && <Button variant="outline" onClick={remove} disabled={busy} data-testid="acc-remove" className="text-rose-600 border-rose-200 hover:bg-rose-50 mr-auto">Remove</Button>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={save} data-testid="acc-save" className="bg-slate-900 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (edit ? "Save changes" : "Save account")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerTable({ scope, accounts, onChanged, readOnlyHint }) {
  const [q, setQ] = useState("");
  const [acc, setAcc] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ scope }); if (q) p.set("q", q); if (acc) p.set("account_id", acc);
    api.get(`/ledger/transactions?${p.toString()}`).then(({ data }) => setRows(data.items || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [scope, q, acc]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const del = async (t) => {
    if (!window.confirm("Delete this transaction?")) return;
    try { await api.delete(`/ledger/transactions/${t.id}`); toast.success("Deleted"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const accName = (id) => accounts.find((a) => a.id === id)?.nickname || "—";

  const [editId, setEditId] = useState(null);
  const [ef, setEf] = useState({});
  const [savedId, setSavedId] = useState(null);
  const startEdit = (t) => {
    setEditId(t.id);
    setEf({ date: t.date, particulars: t.particulars || "", category: t.category || "",
            mode: t.mode || "", reference: t.reference || "",
            amount: String(t.debit || t.credit || "") });
  };
  const saveEdit = async (t) => {
    try {
      await api.patch(`/ledger/transactions/${t.id}`, {
        date: ef.date, particulars: ef.particulars, category: ef.category || null,
        mode: ef.mode || null, reference: ef.reference || null,
        amount: Number(ef.amount) || undefined });
      setEditId(null); setSavedId(t.id); setTimeout(() => setSavedId(null), 1800);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const cell = (k, extra = "") => (
    <Input value={ef[k]} onChange={(e) => setEf((s) => ({ ...s, [k]: e.target.value }))}
      className={"h-7 text-[12px] px-1.5 " + extra} data-testid={`edit-${k}`}
      onKeyDown={(e) => { if (e.key === "Enter") saveEdit({ id: editId }); if (e.key === "Escape") setEditId(null); }} />
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-8" placeholder="Search date, amount, party, description…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ledger-search" />
        </div>
        <Select value={acc} onChange={setAcc} testid="ledger-account-filter" options={accounts.map((a) => [a.id, a.nickname])} placeholder="All accounts" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="ledger-table">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              {["Date","Account","Particulars","Category","Debit","Credit","Mode","Reference",""].map((h, i) => (
                <th key={i} className={"px-3 py-2 font-semibold " + (["Debit","Credit"].includes(h) ? "text-right" : "")}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No transactions.</td></tr>}
            {rows.map((t) => (editId === t.id ? (
              <tr key={t.id} className="bg-amber-50" data-testid={`ledger-edit-row-${t.id}`}>
                <td className="px-2 py-1.5">{cell("date")}</td>
                <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{accName(t.account_id)}</td>
                <td className="px-2 py-1.5">{cell("particulars")}</td>
                <td className="px-2 py-1.5">{cell("category")}</td>
                <td className="px-2 py-1.5" colSpan={2}>{cell("amount", "text-right")}</td>
                <td className="px-2 py-1.5">{cell("mode")}</td>
                <td className="px-2 py-1.5">{cell("reference")}</td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => saveEdit(t)} data-testid={`ledger-save-${t.id}`} className="text-emerald-600 hover:text-emerald-800 mr-2"><Check className="h-4 w-4 inline" /></button>
                  <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4 inline" /></button>
                </td>
              </tr>
            ) : (
              <tr key={t.id} className="hover:bg-slate-50" data-testid={`ledger-row-${t.id}`}>
                <td className="px-3 py-2 whitespace-nowrap text-slate-500">{t.date}</td>
                <td className="px-3 py-2 whitespace-nowrap">{accName(t.account_id)}</td>
                <td className="px-3 py-2">{t.particulars}{t.party ? <span className="text-slate-400"> · {t.party}</span> : null}{t.party_link?.id ? <span className="ml-1 text-[9px] font-semibold uppercase text-sky-600" data-testid={`ledger-partylink-${t.id}`}>{t.party_link.type}</span> : null}{t.type === "TRANSFER" ? <span className="ml-1 text-[10px] text-sky-600">TRANSFER</span> : null}{t.attachment_id ? <a href={`${api.defaults.baseURL}/ledger/attachments/${t.attachment_id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-1.5 inline-flex text-slate-400 hover:text-slate-700" data-testid={`ledger-attach-${t.id}`}><Paperclip className="h-3.5 w-3.5" /></a> : null}</td>
                <td className="px-3 py-2 text-slate-500">{t.category || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700">{t.debit ? fmt(t.debit) : ""}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{t.credit ? fmt(t.credit) : ""}</td>
                <td className="px-3 py-2 text-slate-500">{t.mode || ""}</td>
                <td className="px-3 py-2 text-slate-500 font-mono text-[11px]">{t.reference || ""}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {savedId === t.id && <span className="text-[10px] text-emerald-600 mr-2" data-testid={`ledger-saved-${t.id}`}>SAVED ✓</span>}
                  {!readOnlyHint && !t.reconciled && t.type !== "TRANSFER" && (
                    <button onClick={() => startEdit(t)} data-testid={`ledger-edit-${t.id}`} className="text-slate-400 hover:text-slate-700 mr-2"><Pencil className="h-3.5 w-3.5 inline" /></button>
                  )}
                  {!readOnlyHint && !t.reconciled && (
                    <button onClick={() => del(t)} data-testid={`ledger-del-${t.id}`} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4 inline" /></button>
                  )}
                  {t.reconciled && <span className="text-[10px] text-emerald-600">RECONCILED ✓</span>}
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthView({ scope, accounts }) {
  const now = new Date();
  const [y, setY] = useState(now.getFullYear());
  const [m, setM] = useState(now.getMonth() + 1);
  const [acc, setAcc] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams({ scope, year: y, month: m }); if (acc) p.set("account_id", acc);
    api.get(`/ledger/month?${p.toString()}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [scope, y, m, acc]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(m)} onChange={(v) => setM(Number(v))} testid="month-select" options={MONTHS.map((mn, i) => [String(i + 1), mn])} placeholder="Month" />
        <Select value={String(y)} onChange={(v) => setY(Number(v))} testid="year-select" options={[y, y - 1, y - 2].map((yy) => [String(yy), String(yy)])} placeholder="Year" />
        <Select value={acc} onChange={setAcc} testid="month-account" options={accounts.map((a) => [a.id, a.nickname])} placeholder="All accounts" />
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat testid="month-opening" label="Opening balance" value={data.opening_balance == null ? "—" : fmt(data.opening_balance)} />
            <Stat testid="month-credit" label="Total received" value={fmt(data.total_credit)} tone="text-emerald-700" />
            <Stat testid="month-debit" label="Total paid" value={fmt(data.total_debit)} tone="text-rose-700" />
            <Stat testid="month-closing" label="Closing balance" value={fmt(data.closing_balance)} />
          </div>
          <RecentList rows={data.transactions} />
        </>
      )}
    </div>
  );
}

function YearView({ scope }) {
  const [y, setY] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/ledger/year?scope=${scope}&year=${y}`).then(({ data }) => setData(data)).catch(() => setData(null)); }, [scope, y]);
  return (
    <div className="space-y-3">
      <Select value={String(y)} onChange={(v) => setY(Number(v))} testid="year-view-select" options={[y, y - 1, y - 2].map((yy) => [String(yy), String(yy)])} placeholder="Year" />
      {data && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-[13px]" data-testid="year-table">
            <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2 font-semibold">Month</th><th className="px-4 py-2 font-semibold text-right">Received</th>
              <th className="px-4 py-2 font-semibold text-right">Paid</th><th className="px-4 py-2 font-semibold text-right">Entries</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.months.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No transactions this year.</td></tr>}
              {data.months.map((mm) => (
                <tr key={mm.month} className="hover:bg-slate-50">
                  <td className="px-4 py-2">{(() => { const [yy, mo] = mm.month.split("-"); return `${MONTHS[Number(mo) - 1]} ${yy}`; })()}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{fmt(mm.credit)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-rose-700">{fmt(mm.debit)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{mm.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReportsView({ scope, accounts }) {
  const now = new Date();
  const [y, setY] = useState(now.getFullYear());
  const [m, setM] = useState(0);
  const [acc, setAcc] = useState("");
  const [cats, setCats] = useState(null);
  const [parties, setParties] = useState(null);
  const [drill, setDrill] = useState(null);
  const openParty = async (p) => {
    try {
      const params = new URLSearchParams({ scope, year: y });
      if (m) params.set("month", m);
      if (p.linked && p.party_id) params.set("party_id", p.party_id);
      else params.set("party_text", p.name);
      const { data } = await api.get(`/ledger/reports/party-transactions?${params.toString()}`);
      setDrill({ party: p, ...data });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load"); }
  };
  const exportParty = async (kind) => {
    try {
      const params = new URLSearchParams({ scope, year: y });
      if (m) params.set("month", m);
      const p = drill.party;
      if (p.linked && p.party_id) params.set("party_id", p.party_id);
      else params.set("party_text", p.name);
      const res = await api.get(`/ledger/export/${kind}?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      if (kind === "xlsx") { const a = document.createElement("a"); a.href = url; a.download = `${p.name}.xlsx`; a.click(); }
      else window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
  };
  useEffect(() => {
    const p = new URLSearchParams({ scope, year: y }); if (m) p.set("month", m);
    api.get(`/ledger/reports/category-summary?${p.toString()}`).then(({ data }) => setCats(data.categories || [])).catch(() => setCats([]));
    api.get(`/ledger/reports/party-summary?${p.toString()}`).then(({ data }) => setParties(data.parties || [])).catch(() => setParties([]));
  }, [scope, y, m]);

  const openExport = async (kind) => {
    try {
      const p = new URLSearchParams({ scope, year: y }); if (m) p.set("month", m); if (acc) p.set("account_id", acc);
      const res = await api.get(`/ledger/export/${kind}?${p.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (kind === "pdf" && w) { try { w.addEventListener("load", () => w.print()); } catch {} }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(m)} onChange={(v) => setM(Number(v))} testid="rep-month" options={[["0", "Whole year"], ...MONTHS.map((mn, i) => [String(i + 1), mn])]} placeholder="Period" />
        <Select value={String(y)} onChange={(v) => setY(Number(v))} testid="rep-year" options={[y, y - 1, y - 2].map((yy) => [String(yy), String(yy)])} placeholder="Year" />
        <Select value={acc} onChange={setAcc} testid="rep-account" options={accounts.map((a) => [a.id, a.nickname])} placeholder="All accounts (for export)" />
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => openExport("xlsx")} data-testid="rep-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        <Button variant="outline" size="sm" onClick={() => openExport("pdf")} data-testid="rep-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        <Button variant="outline" size="sm" onClick={() => openExport("pdf")} data-testid="rep-print"><Printer className="h-4 w-4 mr-1" />Print</Button>
      </div>
      <div>
        <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Category summary</h3>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-[13px]" data-testid="category-summary-table">
            <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2 font-semibold">Category</th><th className="px-4 py-2 font-semibold text-right">Received</th><th className="px-4 py-2 font-semibold text-right">Paid</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {cats == null && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">Loading…</td></tr>}
              {cats && cats.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">No data for this period.</td></tr>}
              {cats && cats.map((c) => (
                <tr key={c.category} className="hover:bg-slate-50">
                  <td className="px-4 py-2">{c.category}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{fmt(c.credit)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-rose-700">{fmt(c.debit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Party summary <span className="text-[11px] text-slate-400 font-normal">— totals per customer / vendor / person</span></h3>
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="party-summary-table">
            <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2 font-semibold">Party</th><th className="px-4 py-2 font-semibold">Type</th>
              <th className="px-4 py-2 font-semibold text-right">Received</th><th className="px-4 py-2 font-semibold text-right">Paid</th>
              <th className="px-4 py-2 font-semibold text-right">Entries</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {parties == null && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Loading…</td></tr>}
              {parties && parties.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No parties recorded for this period.</td></tr>}
              {parties && parties.map((p, i) => (
                <tr key={i} className="hover:bg-slate-50 cursor-pointer" onClick={() => openParty(p)} data-testid={`party-row-${i}`}>
                  <td className="px-4 py-2 text-slate-800 hover:underline">{p.name}</td>
                  <td className="px-4 py-2">{p.linked ? <span className="text-[10px] font-semibold uppercase text-sky-600">{p.type}</span> : <span className="text-[10px] text-slate-400">free text</span>}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{fmt(p.received)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-rose-700">{fmt(p.paid)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {drill && (
        <Dialog open onOpenChange={(o) => !o && setDrill(null)}>
          <DialogContent className="max-w-2xl" data-testid="party-drill-dialog">
            <DialogHeader><DialogTitle>{drill.party.name} — transactions</DialogTitle></DialogHeader>
            <div className="flex items-center gap-4 text-[13px] mb-1">
              <span className="text-emerald-700 font-semibold">Received {fmt(drill.received)}</span>
              <span className="text-rose-700 font-semibold">Paid {fmt(drill.paid)}</span>
              <span className="text-slate-400">{drill.items.length} entries</span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => exportParty("xlsx")} data-testid="party-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
              <Button size="sm" variant="outline" onClick={() => exportParty("pdf")} data-testid="party-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
            </div>
            <div className="max-h-[60vh] overflow-auto border rounded-lg">
              <table className="w-full text-[12.5px]">
                <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-500">
                  {["Date","Particulars","Category","Debit","Credit","Ref"].map((h, i) => <th key={i} className={"px-2 py-1.5 " + (["Debit","Credit"].includes(h) ? "text-right" : "")}>{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {drill.items.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">No transactions.</td></tr>}
                  {drill.items.map((t) => (
                    <tr key={t.id}>
                      <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{t.date}</td>
                      <td className="px-2 py-1">{t.particulars}</td>
                      <td className="px-2 py-1 text-slate-500">{t.category || "—"}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-rose-700">{t.debit ? fmt(t.debit) : ""}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{t.credit ? fmt(t.credit) : ""}</td>
                      <td className="px-2 py-1 text-slate-400 font-mono text-[11px]">{t.reference || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
function RecurringView({ scope, accounts, onChanged }) {
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ account_id: "", kind: "PAID", amount: "", day_of_month: 1, particulars: "", category: "", mode: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/ledger/recurring?scope=${scope}`).then(({ data }) => setRows(data.items || [])).catch(() => setRows([])); }, [scope]);
  useEffect(() => { load(); }, [load]);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const create = async () => {
    if (!f.account_id) return toast.error("Select an account");
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter an amount");
    if (!f.particulars.trim()) return toast.error("Enter a description");
    setBusy(true);
    try { await api.post("/ledger/recurring", { scope, ...f, amount: Number(f.amount), day_of_month: Number(f.day_of_month) }); toast.success("Rule saved ✓"); setShow(false); setF({ account_id: "", kind: "PAID", amount: "", day_of_month: 1, particulars: "", category: "", mode: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const post = async (r) => { try { await api.post(`/ledger/recurring/${r.id}/post`); toast.success("Posted this month ✓"); load(); onChanged && onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); } };
  const toggle = async (r) => { try { await api.patch(`/ledger/recurring/${r.id}`, { active: !r.active }); load(); } catch {} };
  const del = async (r) => { if (!window.confirm("Delete this recurring rule?")) return; try { await api.delete(`/ledger/recurring/${r.id}`); load(); } catch {} };
  const accName = (id) => accounts.find((a) => a.id === id)?.nickname || "—";
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-slate-500">Predictable entries (rent, EMI, salary…). We only <b>propose</b> them — nothing is auto-paid.</p>
        <Button size="sm" onClick={() => setShow(true)} data-testid="recurring-add"><Plus className="h-4 w-4 mr-1" />New rule</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="recurring-table">
          <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            {["Description","Account","Type","Amount","Day","Status",""].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows == null && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No recurring rules yet.</td></tr>}
            {rows && rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`recurring-row-${r.id}`}>
                <td className="px-3 py-2">{r.particulars}{r.category ? <span className="text-slate-400"> · {r.category}</span> : null}</td>
                <td className="px-3 py-2">{accName(r.account_id)}</td>
                <td className="px-3 py-2">{r.kind === "PAID" ? <span className="text-rose-700">Paid</span> : <span className="text-emerald-700">Received</span>}</td>
                <td className="px-3 py-2 tabular-nums">{fmt(r.amount)}</td>
                <td className="px-3 py-2">{r.day_of_month}</td>
                <td className="px-3 py-2">
                  {r.due_this_month ? <span className="text-[11px] text-amber-600 font-medium">Due this month</span> : (r.active ? <span className="text-[11px] text-slate-400">Posted</span> : <span className="text-[11px] text-slate-400">Paused</span>)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.due_this_month && <Button size="sm" className="h-7 mr-1 bg-slate-900" onClick={() => post(r)} data-testid={`recurring-post-${r.id}`}>Post now</Button>}
                  <button onClick={() => toggle(r)} className="text-[11px] text-slate-500 hover:text-slate-800 mr-2">{r.active ? "Pause" : "Resume"}</button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && (
        <Dialog open onOpenChange={(o) => !o && setShow(false)}>
          <DialogContent className="max-w-lg" data-testid="recurring-dialog">
            <DialogHeader><DialogTitle>New recurring rule</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Account"><Select value={f.account_id} onChange={(v) => upd("account_id", v)} testid="rec-account" options={accounts.map((a) => [a.id, a.nickname])} placeholder="Select account" /></Field>
              <Field label="Type"><Select value={f.kind} onChange={(v) => upd("kind", v)} testid="rec-kind" options={[["PAID","Money Paid"],["RECEIVED","Money Received"]]} placeholder="Type" /></Field>
              <Field label="Amount"><Input type="number" value={f.amount} onChange={(e) => upd("amount", e.target.value)} data-testid="rec-amount" /></Field>
              <Field label="Day of month (1–28)"><Input type="number" min={1} max={28} value={f.day_of_month} onChange={(e) => upd("day_of_month", e.target.value)} data-testid="rec-day" /></Field>
              <Field label="Description" full><Input value={f.particulars} onChange={(e) => upd("particulars", e.target.value)} data-testid="rec-particulars" placeholder="e.g. Office Rent" /></Field>
              <Field label="Category"><Input value={f.category} onChange={(e) => upd("category", e.target.value)} placeholder="Optional" /></Field>
              <Field label="Mode"><Input value={f.mode} onChange={(e) => upd("mode", e.target.value)} placeholder="Optional" /></Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
              <Button disabled={busy} onClick={create} data-testid="rec-save" className="bg-slate-900 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save rule"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- IMPORT TAB
const MAP_FIELDS = [["date","Date *"],["particulars","Particulars"],["debit","Debit"],["credit","Credit"],["amount","Amount (if single col)"],["reference","Reference"],["category","Category"],["mode","Mode"],["party","Party"]];
function ImportView({ scope, accounts, onChanged }) {
  const [account, setAccount] = useState("");
  const [stage, setStage] = useState("upload"); // upload | map | done
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [archive, setArchive] = useState([]);
  const [viewRows, setViewRows] = useState(null);

  const accName = (id) => accounts.find((a) => a.id === id)?.nickname || "—";
  const loadArchive = useCallback(() => {
    api.get(`/ledger/import/batches?scope=${scope}`).then(({ data }) => setArchive(data.items || [])).catch(() => setArchive([]));
  }, [scope]);
  useEffect(() => { loadArchive(); }, [loadArchive]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!account) { toast.error("Select an account first"); e.target.value = ""; return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/ledger/import/upload?scope=${scope}&account_id=${account}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data); setMapping(data.mapping || {}); setStage("map"); loadArchive();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setBusy(false); e.target.value = ""; }
  };
  const analyze = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/ledger/import/${batch.batch_id}/analyze`, { mapping }); setAnalysis(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Analyze failed"); } finally { setBusy(false); }
  };
  const commit = async (includeDup) => {
    setBusy(true);
    try { const { data } = await api.post(`/ledger/import/${batch.batch_id}/commit?include_duplicates=${includeDup ? "true" : "false"}`); toast.success(`Imported ${data.created} transaction(s) ✓`); setStage("done"); loadArchive(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Commit failed"); } finally { setBusy(false); }
  };
  const rollback = async (b) => {
    if (!window.confirm(`Undo import "${b.filename}"? This removes its imported (non-reconciled) transactions.`)) return;
    try { const { data } = await api.post(`/ledger/import/${b.id}/rollback`); toast.success(`Removed ${data.deleted} transaction(s)`); loadArchive(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
  };
  const download = async (b) => {
    try { const res = await api.get(`/ledger/import/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = b.filename || "import"; a.click(); setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };
  const openRows = async (b) => {
    try { const { data } = await api.get(`/ledger/import/${b.id}/rows`); setViewRows({ batch: b, ...data }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load rows"); }
  };
  const reset = () => { setStage("upload"); setBatch(null); setAnalysis(null); setMapping({}); };

  const STATUS_TONE = { STAGED: "text-slate-500", ANALYZED: "text-sky-600", COMMITTED: "text-emerald-600", ROLLED_BACK: "text-rose-500" };

  return (
    <div className="space-y-4" data-testid="import-view">
      <p className="text-[13px] text-slate-500">Bring old spreadsheets in cleanly: upload, map the columns, review, then commit. Nothing is imported until you confirm.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={account} onChange={setAccount} testid="import-account" options={accounts.map((a) => [a.id, a.nickname])} placeholder="Import into account…" />
        {stage === "upload" && (
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-[13px] font-medium cursor-pointer" data-testid="import-upload-btn">
            <Upload className="h-4 w-4" />{busy ? "Uploading…" : "Upload CSV / Excel"}
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} disabled={busy} />
          </label>
        )}
        {stage !== "upload" && <Button variant="outline" size="sm" onClick={reset}>Start over</Button>}
      </div>

      {stage === "map" && batch && (
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[13px] font-semibold text-slate-700 mb-2">Map your columns ({batch.row_count} rows in {batch.filename})</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {MAP_FIELDS.map(([k, label]) => (
                <Field key={k} label={label}>
                  <Select value={mapping[k] || ""} onChange={(v) => setMapping((mm) => ({ ...mm, [k]: v || null }))} testid={`map-${k}`} options={batch.headers.map((h) => [h, h])} placeholder="—" />
                </Field>
              ))}
            </div>
            <Button className="mt-3 bg-slate-900" size="sm" disabled={busy} onClick={analyze} data-testid="import-analyze">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analyze"}</Button>
          </div>

          {analysis && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="import-analysis">
              <div className="flex gap-3 text-[13px]">
                <span className="text-emerald-700 font-semibold">{analysis.summary.ok} ready</span>
                <span className="text-amber-600 font-semibold">{analysis.summary.duplicate} duplicate</span>
                <span className="text-rose-600 font-semibold">{analysis.summary.error} error</span>
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto border rounded-lg">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-500">
                    {["#","Status","Date","Particulars","Debit","Credit","Ref"].map((h, i) => <th key={i} className={"px-2 py-1.5 " + (h === "Debit" || h === "Credit" ? "text-right" : "")}>{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {analysis.preview.map((r) => (
                      <tr key={r.row_no}>
                        <td className="px-2 py-1 text-slate-400">{r.row_no}</td>
                        <td className="px-2 py-1"><span className={r.status === "OK" ? "text-emerald-600" : r.status === "DUPLICATE" ? "text-amber-600" : "text-rose-600"}>{r.status}</span></td>
                        <td className="px-2 py-1">{r.date || "—"}</td>
                        <td className="px-2 py-1 truncate max-w-[220px]">{r.particulars}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{r.debit || ""}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{r.credit || ""}</td>
                        <td className="px-2 py-1 text-slate-400">{r.reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-slate-900" disabled={busy || !analysis.summary.ok} onClick={() => commit(false)} data-testid="import-commit">Import {analysis.summary.ok} ready row(s)</Button>
                {analysis.summary.duplicate > 0 && <Button size="sm" variant="outline" disabled={busy} onClick={() => commit(true)}>Include duplicates</Button>}
              </div>
            </div>
          )}
        </div>
      )}
      {stage === "done" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-[14px] text-emerald-800" data-testid="import-done">
          Import complete. <button className="underline" onClick={reset}>Import another file</button>
        </div>
      )}

      {/* Old Excel Archive — read-only history of every imported file */}
      <div>
        <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Import archive</h3>
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="import-archive-table">
            <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              {["File","When","Account","Rows","Imported","Status",""].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {archive.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No imports yet.</td></tr>}
              {archive.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50" data-testid={`archive-row-${b.id}`}>
                  <td className="px-3 py-2 truncate max-w-[220px]">{b.filename}</td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{(b.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2">{b.account_name || accName(b.account_id)}</td>
                  <td className="px-3 py-2 tabular-nums">{b.row_count}</td>
                  <td className="px-3 py-2 tabular-nums">{b.committed || 0}</td>
                  <td className={"px-3 py-2 text-[11px] font-semibold " + (STATUS_TONE[b.status] || "")}>{b.status}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openRows(b)} className="text-slate-500 hover:text-slate-800 mr-3 text-[12px]" data-testid={`archive-view-${b.id}`}>View</button>
                    <button onClick={() => download(b)} className="text-slate-500 hover:text-slate-800 mr-3 text-[12px]" data-testid={`archive-download-${b.id}`}>Download</button>
                    {b.status === "COMMITTED" && <button onClick={() => rollback(b)} className="text-rose-500 hover:text-rose-700 text-[12px]" data-testid={`archive-rollback-${b.id}`}>Undo</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewRows && (
        <Dialog open onOpenChange={(o) => !o && setViewRows(null)}>
          <DialogContent className="max-w-3xl" data-testid="archive-rows-dialog">
            <DialogHeader><DialogTitle>{viewRows.batch.filename} — original rows (read-only)</DialogTitle></DialogHeader>
            <div className="max-h-[60vh] overflow-auto border rounded-lg">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-500">
                  <th className="px-2 py-1.5">#</th>
                  {(viewRows.batch.headers || Object.keys(viewRows.rows[0]?.raw || {})).map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {viewRows.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-1 text-slate-400">{r.row_no}</td>
                      {(viewRows.batch.headers || Object.keys(r.raw)).map((h) => <td key={h} className="px-2 py-1">{String(r.raw?.[h] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ------------------------------------------------------------- RECONCILE TAB
function ReconcileView({ scope, accounts, onChanged }) {
  const [account, setAccount] = useState("");
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statements, setStatements] = useState([]);

  const loadStatements = useCallback(() => {
    api.get(`/ledger/recon/statements?scope=${scope}`).then(({ data }) => setStatements(data.items || [])).catch(() => setStatements([]));
  }, [scope]);
  useEffect(() => { loadStatements(); }, [loadStatements]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!account) { toast.error("Select an account first"); e.target.value = ""; return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/ledger/recon/upload?scope=${scope}&account_id=${account}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadView(data.statement_id); loadStatements();
      toast.success("Statement matched ✓");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setBusy(false); e.target.value = ""; }
  };
  const loadView = async (sid) => { const { data } = await api.get(`/ledger/recon/${sid}`); setView(data); };
  const confirm = async (bank_line_id, ledger_txn_id) => { try { await api.post("/ledger/recon/confirm", { bank_line_id, ledger_txn_id }); await loadView(view.statement.id); onChanged && onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const createFromBank = async (bank_line_id) => { try { await api.post("/ledger/recon/create-from-bank", { bank_line_id }); await loadView(view.statement.id); onChanged && onChanged(); toast.success("Ledger entry created ✓"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const delStatement = async (s) => {
    if (!window.confirm(`Remove statement "${s.filename}"? Any entries reconciled against it will be marked unreconciled.`)) return;
    try { const { data } = await api.delete(`/ledger/recon/statements/${s.id}`); toast.success(`Removed${data.unreconciled ? ` · ${data.unreconciled} unreconciled` : ""}`); if (view?.statement?.id === s.id) setView(null); loadStatements(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  const rematch = async (s) => {
    try { const { data } = await api.post(`/ledger/recon/${s.id}/rematch`); toast.success(`Re-matched · ${data.counts.MATCHED} matched`); await loadView(s.id); loadStatements(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not re-match"); }
  };
  const confirmAll = async () => {
    if (!view?.statement?.id) return;
    try { const { data } = await api.post(`/ledger/recon/${view.statement.id}/confirm-all`); toast.success(`Confirmed ${data.confirmed} match(es) ✓`); await loadView(view.statement.id); loadStatements(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not confirm"); }
  };

  const tone = { MATCHED: "text-emerald-600", POSSIBLE: "text-amber-600", BANK_ONLY: "text-slate-500" };
  return (
    <div className="space-y-4" data-testid="reconcile-view">
      <p className="text-[13px] text-slate-500">Upload a bank statement (CSV / Excel) and we’ll match it against your ledger. PDF statements can be attached to entries but aren’t auto-read.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={account} onChange={setAccount} testid="recon-account" options={accounts.map((a) => [a.id, a.nickname])} placeholder="Reconcile account…" />
        <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-[13px] font-medium cursor-pointer" data-testid="recon-upload-btn">
          <Upload className="h-4 w-4" />{busy ? "Working…" : "Upload statement"}
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} disabled={busy} />
        </label>
      </div>

      {/* Statement history */}
      {statements.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="statement-history">
          <table className="w-full text-[13px]">
            <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              {["Statement","Account","When","Lines","Matched",""].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {statements.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`statement-row-${s.id}`}>
                  <td className="px-3 py-2 truncate max-w-[220px]">{s.filename}</td>
                  <td className="px-3 py-2">{s.account_name}</td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 tabular-nums">{s.line_count}</td>
                  <td className="px-3 py-2 tabular-nums text-emerald-700">{s.matched}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => loadView(s.id)} className="text-slate-500 hover:text-slate-800 mr-3 text-[12px]" data-testid={`statement-open-${s.id}`}>Open</button>
                    <button onClick={() => rematch(s)} className="text-sky-600 hover:text-sky-800 mr-3 text-[12px]" data-testid={`statement-rematch-${s.id}`}>Re-match</button>
                    <button onClick={() => delStatement(s)} className="text-rose-500 hover:text-rose-700 text-[12px]" data-testid={`statement-delete-${s.id}`}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view && (
        <>
        {view.lines.some((l) => l.match_status === "MATCHED") && (
          <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="recon-confirm-all-bar">
            <span className="text-[12.5px] text-emerald-800">Clean matches found — confirm them all to tick them off at once.</span>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={confirmAll} data-testid="recon-confirm-all"><Check className="h-4 w-4 mr-1" />Confirm all exact matches</Button>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 text-[12px] font-semibold text-slate-600">Bank statement lines</div>
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto" data-testid="recon-bank-lines">
              {view.lines.map((l) => (
                <div key={l.id} className="px-3 py-2 flex items-center gap-2 text-[12.5px]">
                  <span className="text-slate-400 w-20 shrink-0">{l.date}</span>
                  <span className="flex-1 truncate">{l.description || "—"}<span className="block text-[10px] text-slate-400">{l.reference}</span></span>
                  <span className={"tabular-nums " + (l.debit ? "text-rose-700" : "text-emerald-700")}>{l.debit ? "−" + fmt(l.debit) : "+" + fmt(l.credit)}</span>
                  <span className={"text-[10px] font-semibold w-16 text-right " + (tone[l.match_status] || "")}>{l.match_status === "BANK_ONLY" ? "BANK ONLY" : l.match_status}</span>
                  {l.match_status === "POSSIBLE" && l.matched_txn_id && (
                    <button className="text-emerald-600 hover:text-emerald-800" title="Confirm match" onClick={() => confirm(l.id, l.matched_txn_id)} data-testid={`recon-confirm-${l.id}`}><Check className="h-4 w-4" /></button>
                  )}
                  {l.match_status === "BANK_ONLY" && (
                    <button className="text-sky-600 hover:text-sky-800" title="Create ledger entry" onClick={() => createFromBank(l.id)} data-testid={`recon-create-${l.id}`}><Plus className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 text-[12px] font-semibold text-slate-600">In your ledger but not on the statement</div>
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto" data-testid="recon-ledger-only">
              {view.ledger_only.length === 0 && <div className="px-3 py-6 text-center text-slate-400 text-[12px]">Nothing outstanding.</div>}
              {view.ledger_only.map((t) => (
                <div key={t.id} className="px-3 py-2 flex items-center gap-2 text-[12.5px]">
                  <span className="text-slate-400 w-20 shrink-0">{t.date}</span>
                  <span className="flex-1 truncate">{t.particulars}</span>
                  <span className={"tabular-nums " + (t.debit ? "text-rose-700" : "text-emerald-700")}>{t.debit ? "−" + fmt(t.debit) : "+" + fmt(t.credit)}</span>
                  <span className="text-[10px] text-amber-600 font-semibold">LEDGER ONLY</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

